import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import {
  loadKnowledgeBase,
  saveKnowledgeEntry,
  toggleKnowledgeEntryActive,
  deleteKnowledgeEntry,
  type IvrKnowledgeEntry,
} from '@/lib/ivrKnowledgeBase';
import { Plus, Trash2, Edit, Save, X, RefreshCw, GraduationCap } from 'lucide-react';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

interface FormState {
  id?: string;
  question: string;
  answer: string;
  sort_order: string;
  is_active: boolean;
}

const emptyForm = (): FormState => ({ question: '', answer: '', sort_order: '100', is_active: true });

const IvrBotTrainingTab: React.FC = () => {
  const [entries, setEntries] = useState<IvrKnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const load = async () => {
    setLoading(true);
    const data = await loadKnowledgeBase(DEFAULT_TENANT_ID);
    setEntries(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const startEdit = (e: IvrKnowledgeEntry) => {
    setForm({ id: e.id, question: e.question, answer: e.answer, sort_order: String(e.sort_order ?? 100), is_active: e.is_active });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast({ title: 'Question and answer required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await saveKnowledgeEntry(
        { id: form.id, question: form.question, answer: form.answer, sort_order: Number(form.sort_order) || 100, is_active: form.is_active },
        DEFAULT_TENANT_ID,
      );
      toast({ title: 'Saved' });
      setShowForm(false);
      setForm(emptyForm());
      await load();
    } catch (e) {
      toast({ title: 'Not saved', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    }
    setSaving(false);
  };

  const toggleActive = async (e: IvrKnowledgeEntry) => {
    try {
      await toggleKnowledgeEntryActive(e);
      await load();
    } catch (err) {
      toast({ title: 'Not updated', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    }
  };

  const remove = async (e: IvrKnowledgeEntry) => {
    if (!window.confirm(`Delete this Q&A entry?\n\n"${e.question}"`)) return;
    try {
      await deleteKnowledgeEntry(e.id);
      await load();
    } catch (err) {
      toast({ title: 'Not deleted', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-yellow-500" />
            Bot Training
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Specific questions and answers for the IVR to draw on -- fare estimates, cancellation policy, payment
            methods, anything callers regularly ask. This is DMS's side of the data only: the IVR voice bot is a
            separate deployment, so saving an entry here doesn't make it answer callers by itself -- the IVR
            developer needs to wire the bot up to fetch this list (via the get-ivr-knowledge-base endpoint) and use
            it in conversation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button onClick={startAdd} className="golden-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Q&amp;A
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-foreground">{form.id ? 'Edit Q&A' : 'Add Q&A'}</h4>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div>
            <Label className="text-yellow-500">Question</Label>
            <Input
              value={form.question}
              onChange={(e) => setForm((prev) => ({ ...prev, question: e.target.value }))}
              placeholder="e.g. How much does a trip from the airport to town cost?"
            />
          </div>
          <div>
            <Label className="text-yellow-500">Answer</Label>
            <Textarea
              value={form.answer}
              onChange={(e) => setForm((prev) => ({ ...prev, answer: e.target.value }))}
              placeholder="e.g. Fares are metered, but a typical airport-to-town trip runs around $45-55 depending on traffic."
              className="min-h-[90px]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-yellow-500">Sort Order</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 text-sm md:col-span-2">
              <Checkbox checked={form.is_active} onCheckedChange={(c) => setForm((prev) => ({ ...prev, is_active: Boolean(c) }))} />
              <span>Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="golden-button">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No Q&amp;A entries yet. Add one to get started.</p>
        ) : (
          entries.map((e) => (
            <div key={e.id} className={`rounded-lg border border-border bg-background/40 p-3 ${!e.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{e.question}</p>
                  <p className="text-sm text-muted-foreground mt-1">{e.answer}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleActive(e)}
                    className={`rounded-full border px-2 py-1 text-xs font-medium ${
                      e.is_active ? 'border-green-700/50 bg-green-900/30 text-green-300' : 'border-gray-700 bg-gray-900/40 text-gray-400'
                    }`}
                  >
                    {e.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(e)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => remove(e)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default IvrBotTrainingTab;
